// useSubjectName — small hook that reads the active subject for the
// caregiver-mode pronoun flip (round-3 fix #4). When profileKind=='self'
// (or the user hasn't picked yet) it returns null; the caller falls back
// to the second-person "Your medicines" copy. When profileKind=='caregiver'
// with a name set, it returns the name so titles can read "Marv's
// medicines" / "Marv's safety flags".
//
// Round-3 finding: Mei, Priya, Gloria all picked "A loved one" on the
// first-run prompt and noticed downstream pages still said "your". The
// prompt asked the right question; this hook is the answer.

import { useMe } from './useMe';

export interface SubjectContext {
  /** First name of the active subject, possessive form. e.g. "Marv's" */
  possessive: string | null;
  /** First name of the active subject, no apostrophe. e.g. "Marv" */
  name: string | null;
  /** True when the user has declared caregiver mode and named a subject. */
  isCaregiverMode: boolean;
}

export function useSubjectName(): SubjectContext {
  const { me } = useMe();
  if (!me?.accessibility) {
    return { possessive: null, name: null, isCaregiverMode: false };
  }
  const isCaregiverMode = me.accessibility.profileKind === 'caregiver';
  const name = me.accessibility.profileSubjectName?.trim() || null;
  if (!isCaregiverMode || !name) {
    return { possessive: null, name: null, isCaregiverMode: false };
  }
  // English possessive: names ending in 's' add only an apostrophe.
  const possessive = name.endsWith('s') ? `${name}'` : `${name}'s`;
  return { possessive, name, isCaregiverMode: true };
}

/** Convenience: returns "Marv's medicines" or "Your medicines" based on caregiver state. */
export function subjectTitle(subject: SubjectContext, secondPersonNoun: string): string {
  if (subject.possessive) return `${subject.possessive} ${secondPersonNoun}`;
  return `Your ${secondPersonNoun}`;
}
